import React from 'react';
import { Bar } from 'react-chartjs-2';
import './Modal.css';

const PostcodeSummaryModal = ({ isOpen, onClose, postcodeData, postcodeSector, onViewDestinations }) => {
  if (!isOpen || !postcodeData) return null;

  // Function to export data as CSV
  const exportCSV = () => {
    const csvRows = [];

    // Header
    csvRows.push('Postcode Sector Summary');
    csvRows.push(`Postcode Sector,${postcodeSector}`);
    if (postcodeData.population) {
      csvRows.push(`Population,${postcodeData.population}`);
    }
    csvRows.push('');

    // Demographics data
    csvRows.push('Life Stage & Affluence');
    csvRows.push('Category,Value');
    csvRows.push(`Life Stage,${postcodeData.age?.['Life Stage'] || 'Unknown'}`);
    csvRows.push(`Affluence,${postcodeData.income?.['Affluence'] || 'Unknown'}`);
    csvRows.push('');

    csvRows.push('Social Grade Demographics');
    csvRows.push('Social Grade,Percentage');
    Object.entries(postcodeData.social_grade || {}).forEach(([key, value]) => {
      csvRows.push(`${key},${value.toFixed(2)}`);
    });
    csvRows.push('');

    if (postcodeData.spend_total && Object.keys(postcodeData.spend_total).length > 0) {
      csvRows.push('Total Spend by Category');
      csvRows.push('Category,Total Spend (£)');
      Object.entries(postcodeData.spend_total).forEach(([key, value]) => {
        csvRows.push(`${key},${value.toFixed(2)}`);
      });
      csvRows.push('');
    }

    if (postcodeData.spend_per_person && Object.keys(postcodeData.spend_per_person).length > 0) {
      csvRows.push('Spend per Person by Category');
      csvRows.push('Category,Spend per Person (£)');
      Object.entries(postcodeData.spend_per_person).forEach(([key, value]) => {
        csvRows.push(`${key},${value.toFixed(2)}`);
      });
      csvRows.push('');
    }

    csvRows.push('Tenure Demographics');
    csvRows.push('Tenure Type,Percentage');
    Object.entries(postcodeData.tenure || {}).forEach(([key, value]) => {
      csvRows.push(`${key},${value.toFixed(2)}`);
    });
    csvRows.push('');

    csvRows.push('Employment Demographics');
    csvRows.push('Employment Type,Percentage');
    Object.entries(postcodeData.employment || {}).forEach(([key, value]) => {
      csvRows.push(`${key},${value.toFixed(2)}`);
    });

    // Create blob and download
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `postcode_${postcodeSector.replace(/\s+/g, '_')}_summary.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Chart configurations
  const socialGradeChartData = {
    labels: Object.keys(postcodeData.social_grade || {}),
    datasets: [{
      label: 'Social Grade Distribution (%)',
      data: Object.values(postcodeData.social_grade || {}),
      backgroundColor: 'rgba(153, 102, 255, 0.6)',
      borderColor: 'rgba(153, 102, 255, 1)',
      borderWidth: 1
    }]
  };

  const spendTotalChartData = {
    labels: Object.keys(postcodeData.spend_total || {}),
    datasets: [{
      label: 'Total Spend by Category (£)',
      data: Object.values(postcodeData.spend_total || {}),
      backgroundColor: 'rgba(75, 192, 192, 0.6)',
      borderColor: 'rgba(75, 192, 192, 1)',
      borderWidth: 1
    }]
  };

  const spendPerPersonChartData = {
    labels: Object.keys(postcodeData.spend_per_person || {}),
    datasets: [{
      label: 'Spend per Person by Category (£)',
      data: Object.values(postcodeData.spend_per_person || {}),
      backgroundColor: 'rgba(54, 162, 235, 0.6)',
      borderColor: 'rgba(54, 162, 235, 1)',
      borderWidth: 1
    }]
  };

  const tenureChartData = {
    labels: Object.keys(postcodeData.tenure || {}),
    datasets: [{
      label: 'Tenure Distribution (%)',
      data: Object.values(postcodeData.tenure || {}),
      backgroundColor: 'rgba(255, 159, 64, 0.6)',
      borderColor: 'rgba(255, 159, 64, 1)',
      borderWidth: 1
    }]
  };

  const employmentChartData = {
    labels: Object.keys(postcodeData.employment || {}),
    datasets: [{
      label: 'Employment Distribution (%)',
      data: Object.values(postcodeData.employment || {}),
      backgroundColor: 'rgba(255, 99, 132, 0.6)',
      borderColor: 'rgba(255, 99, 132, 1)',
      borderWidth: 1
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return context.parsed.y.toFixed(1) + '%';
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return value.toFixed(1) + '%';
          }
        }
      }
    }
  };

  const spendChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return '£' + Math.round(context.parsed.y).toLocaleString();
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return '£' + Math.round(value).toLocaleString();
          }
        }
      }
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Postcode Summary: {postcodeSector}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <div className="demographics-section">
            <h3>Demographics Profile</h3>

            <div style={{ marginBottom: '2rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
              <h4>Life Stage & Affluence</h4>
              {postcodeData.population && (
                <p><strong>Population:</strong> {postcodeData.population.toLocaleString()}</p>
              )}
              <p><strong>Life Stage:</strong> {postcodeData.age?.['Life Stage'] || 'Unknown'}</p>
              <p><strong>Affluence Level:</strong> {postcodeData.income?.['Affluence'] || 'Unknown'}</p>
            </div>

            <div className="chart-container" style={{ height: '300px', marginBottom: '2rem' }}>
              <h4>Social Grade Distribution</h4>
              <Bar data={socialGradeChartData} options={chartOptions} />
            </div>

            {postcodeData.spend_total && Object.keys(postcodeData.spend_total).length > 0 && (
              <div className="chart-container" style={{ height: '300px', marginBottom: '2rem' }}>
                <h4>Total Spend by Category</h4>
                <Bar data={spendTotalChartData} options={spendChartOptions} />
              </div>
            )}

            {postcodeData.spend_per_person && Object.keys(postcodeData.spend_per_person).length > 0 && (
              <div className="chart-container" style={{ height: '300px', marginBottom: '2rem' }}>
                <h4>Spend per Person by Category</h4>
                <Bar data={spendPerPersonChartData} options={spendChartOptions} />
              </div>
            )}

            <div className="chart-container" style={{ height: '300px', marginBottom: '2rem' }}>
              <h4>Tenure Distribution</h4>
              <Bar data={tenureChartData} options={chartOptions} />
            </div>

            <div className="chart-container" style={{ height: '300px', marginBottom: '2rem' }}>
              <h4>Employment Distribution</h4>
              <Bar data={employmentChartData} options={chartOptions} />
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={exportCSV}>
            Export CSV
          </button>
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
          <button className="btn-primary" onClick={onViewDestinations}>
            View Postcode Destinations
          </button>
        </div>
      </div>
    </div>
  );
};

export default PostcodeSummaryModal;
